import type { SearchHitKind } from "@api/generated/model";
import { useWorkspaceSearch } from "@api/generated/workspace/workspace";
import { Icon } from "@app/icons";
import { allItems } from "@app/nav";
import { useRecent, useSeason } from "@app/workspace";
import { Kbd, Modal, TextInput } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useRouter } from "@tanstack/react-router";
import { fold } from "@ui/format";
import { QueryFeedback } from "@ui/QueryFeedback";
import { type KeyboardEvent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import css from "./shell.module.css";
import { hitTarget } from "./targets";

type Kind = SearchHitKind | "screen";
type Entry = { key: string; kind: Kind; title: string; subtitle: string; href: string };

const ORDER: Kind[] = ["plot", "genotype", "trial", "screen", "partner", "query"];
const MONO = new Set<Kind>(["genotype", "plot"]);

/** The Ctrl K quick search: server hits (plots, genotypes, trials, partners, saved queries) and the screens. */
export function CommandPalette({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const router = useRouter();
  const { ev } = useSeason();
  const { push } = useRecent();
  const [text, setText] = useState("");
  const [active, setActive] = useState(0);
  const [debounced] = useDebouncedValue(text.trim(), 150);
  const found = useWorkspaceSearch({ q: debounced, ev: ev ?? 0 }, { query: { enabled: opened && debounced.length >= 2 && !!ev } });

  const entries = useMemo<Entry[]>(() => {
    const q = fold(debounced);
    const screens: Entry[] = q
      ? allItems()
          .filter(({ section, item }) =>
            fold(`${t(`nav.${section.key}.${item.id}`)} ${t(`nav.${section.key}.title`)} ${item.keywords ?? ""}`).includes(q),
          )
          .slice(0, 6)
          .map(({ section, item }) => ({
            key: `f:${item.to}`,
            kind: "screen",
            title: t(`nav.${section.key}.${item.id}`),
            subtitle: t(`nav.${section.key}.title`),
            href: item.to,
          }))
      : [];
    const hits = (found.data ?? []).flatMap((h, i): Entry[] => {
      const target = hitTarget(h);
      return target
        ? [{ key: `${h.kind}:${i}`, kind: h.kind, title: h.title, subtitle: h.subtitle, href: router.buildLocation(target).href }]
        : [];
    });
    return [...hits, ...screens].sort((a, b) => ORDER.indexOf(a.kind) - ORDER.indexOf(b.kind));
  }, [found.data, debounced, t, router]);

  const current = entries[Math.min(active, entries.length - 1)];
  const close = () => {
    setText("");
    setActive(0);
    onClose();
  };
  const open = (entry: Entry | undefined) => {
    if (!entry) return;
    close();
    if (entry.kind !== "screen") push({ kind: entry.kind, title: entry.title, subtitle: entry.subtitle, href: entry.href });
    void router.navigate({ href: entry.href });
  };
  const onKey = (e: KeyboardEvent) => {
    const step = { ArrowDown: 1, ArrowUp: -1 }[e.key];
    if (step) {
      e.preventDefault();
      setActive((a) => Math.max(0, Math.min(a + step, entries.length - 1)));
    } else if (e.key === "Enter") {
      e.preventDefault();
      open(current);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={close}
      withCloseButton={false}
      size={880}
      padding={0}
      yOffset="10vh"
      attributes={{ content: { "aria-label": t("palette.title") } }}
    >
      <div style={{ padding: 12, borderBottom: "1px solid var(--b-border)" }}>
        <TextInput
          data-autofocus
          size="md"
          value={text}
          onChange={(e) => {
            setText(e.currentTarget.value);
            setActive(0);
          }}
          onKeyDown={onKey}
          placeholder={t("palette.placeholder")}
          leftSection={<Icon name="search" size={18} />}
          aria-label={t("palette.title")}
        />
      </div>
      <QueryFeedback busy={found.isFetching} error={found.error} retry={found.refetch} />
      <div className={css.palette}>
        <div className={css.paletteList} role="listbox" aria-label={t("palette.title")}>
          {(!debounced || (!found.isFetching && !found.isError && entries.length === 0)) && (
            <p className="mu sm" style={{ padding: "12px 10px" }}>
              {debounced ? t("palette.empty") : t("palette.typeHint")}
            </p>
          )}
          {entries.map((e, i) => (
            <div key={e.key}>
              {e.kind !== entries[i - 1]?.kind && <div className={`sec ${css.paletteGroup}`}>{t(`palette.group.${e.kind}`)}</div>}
              <button
                type="button"
                role="option"
                aria-selected={i === active}
                className={`${css.hit} ${i === active ? css.on : ""}`}
                onMouseEnter={() => setActive(i)}
                onClick={() => open(e)}
              >
                <span className={css.hitText}>
                  <span className={css.hitTitle}>{e.title}</span>
                  <span className={`${css.hitSub} ${MONO.has(e.kind) ? "mono" : ""}`}>{e.subtitle}</span>
                </span>
                <span className="chip">{t(`palette.kind.${e.kind}`)}</span>
              </button>
            </div>
          ))}
        </div>
        <aside className={css.preview} aria-label={t("palette.preview")}>
          {current ? (
            <>
              <span className="sec">{t(`palette.kind.${current.kind}`)}</span>
              <b style={{ fontSize: "1.125rem", fontWeight: 600 }}>{current.title}</b>
              <span className={`mu ${MONO.has(current.kind) ? "mono" : "sm"}`}>{current.subtitle}</span>
              <div style={{ flexGrow: 1 }} />
              <span className="sm mu">
                <Kbd>Enter</Kbd> {t("palette.open")}
              </span>
            </>
          ) : (
            <span className="sm mu">{t("palette.typeHint")}</span>
          )}
        </aside>
      </div>
      <div className={css.paletteFoot}>
        <span>
          <Kbd>↑</Kbd> <Kbd>↓</Kbd> {t("palette.navigate")}
        </span>
        <span>
          <Kbd>Enter</Kbd> {t("palette.open")}
        </span>
        <span>
          <Kbd>Esc</Kbd> {t("palette.close")}
        </span>
      </div>
    </Modal>
  );
}
